// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT DIRECTLY.

import AdminPortal, { processAdminPortal } from "entity/AdminPortal";
import Department from "entity/Department";
import InviteInfo, { processInviteInfoArray } from "entity/InviteInfo";
import LicenseType from "enums/LicenseType";
import Location from "entity/Location";
import Member, { processMember, processMemberArray } from "entity/Member";
import MemberType from "enums/MemberType";
import Paging from "./Paging";
import Team from "entity/Team";
import User from "entity/User";

import { QueryParams, RestController, RestRequest } from "./RestController";

export default class AdminPortalController extends RestController {

    /*
     Claim Admin Portal. Returns claimed with state and privilege
     */

    claimAdminPortal(): Promise<AdminPortal> {
        const request = new RestRequest("/api/v1/admin/portal/claim", "POST", null, undefined);
        return this.request(request, processAdminPortal) as Promise<AdminPortal>;
    }

    /*
     Create a department
     */

    createDepartment(department: Department): Promise<Department> {
        const request = new RestRequest("/api/v1/admin/portal/department", "POST", department, undefined);
        return this.request(request, null) as Promise<Department>;
    }

    /*
     Create a location
     */

    createLocation(location: Location): Promise<Location> {
        const request = new RestRequest("/api/v1/admin/portal/location", "POST", location, undefined);
        return this.request(request, null) as Promise<Location>;
    }

    /*
     Create a SCIM key for configuring Azure Active Directory integration.
     */

    createScimKey(): Promise<string> {
        const request = new RestRequest("/api/v1/admin/portal/scim/create", "POST", null, undefined);
        return this.request(request, null) as Promise<string>;
    }

    /*
     Create a team
     */

    createTeam(team: Team): Promise<Team> {
        const request = new RestRequest("/api/v1/admin/portal/team", "POST", team, undefined);
        return this.request(request, null) as Promise<Team>;
    }

    /*
     Delete cover image for team or company.
     */

    deleteCoverImage(): Promise<void> {
        const request = new RestRequest("/api/v1/admin/portal/cover/image", "DELETE", null, undefined);
        return this.requestVoid(request);
    }

    /*
     Delete logo image for team or company.
     */

    deleteLogoImage(): Promise<void> {
        const request = new RestRequest("/api/v1/admin/portal/logo/image", "DELETE", null, undefined);
        return this.requestVoid(request);
    }

    /*
     Get team members.
     */

    findMembers(joined: boolean, text?: string, page?: Paging): Promise<Member[]> {
        const queryParams: QueryParams = { };
        if (text !== undefined) {
          queryParams.text = text;
        }
        const request = new RestRequest("/api/v1/admin/portal/find/member/" + joined + "", "POST", null, queryParams);
        if (page) request.addPaging("", page);

        return this.request(request, processMemberArray) as Promise<Member[]>;
    }

    /*
     Get Admin Portal with state and privilege
     */

    getAdminPortal(): Promise<AdminPortal> {
        const request = new RestRequest("/api/v1/admin/portal", "GET", null, undefined);
        return this.request(request, processAdminPortal) as Promise<AdminPortal>;
    }

    /*
     Get allowed email domains.
     */

    getEmailDomains(): Promise<string[]> {
        const request = new RestRequest("/api/v1/admin/portal/email/domain", "GET", null, undefined);
        return this.request(request, null) as Promise<string[]>;
    }

    /*
     Get report URL
     */

    getReportUrl(): Promise<string> {
        const request = new RestRequest("/api/v1/admin/portal/report/url", "GET", null, undefined);
        return this.request(request, null) as Promise<string>;
    }

    /*
     Get current SCIM key for configuring Azure Active Directory integration.
     */

    getScimKey(): Promise<string> {
        const request = new RestRequest("/api/v1/admin/portal/scim", "GET", null, undefined);
        return this.request(request, null) as Promise<string>;
    }

    /*
     Get teams.
     */

    getTeams(page?: Paging): Promise<Team[]> {
        const request = new RestRequest("/api/v1/admin/portal/team", "GET", null, undefined);
        if (page) request.addPaging("", page);

        return this.request(request, null) as Promise<Team[]>;
    }

    /*
     Send team invitation email to a team member.
     */

    inviteExistingMember(member: Member): Promise<string> {
        const request = new RestRequest("/api/v1/admin/portal/invite/existing/member", "POST", member, undefined);
        return this.request(request, null) as Promise<string>;
    }

    /*
     Send team invitation email to a user.
     */

    inviteMember(member: User): Promise<string> {
        const request = new RestRequest("/api/v1/admin/portal/invite/member", "POST", member, undefined);
        return this.request(request, null) as Promise<string>;
    }

    /*
     Send team invitation email to users.
     */

    inviteMembers(role: MemberType, license: LicenseType, emails: string[]): Promise<void> {
        const request = new RestRequest("/api/v1/admin/portal/invite/members/" + role + "/" + license + "", "POST", emails, undefined);
        return this.requestVoid(request);
    }

    /*
     Check if user is an admin
     */

    isAdmin(): Promise<boolean> {
        const request = new RestRequest("/api/v1/admin/is/admin", "GET", null, undefined);
        return this.request(request, null) as Promise<boolean>;
    }

    /*
     Join a team
     */

    joinTeam(invitationId: string): Promise<string> {
        const request = new RestRequest("/api/v1/admin/portal/join/team/" + invitationId + "", "POST", null, undefined);
        return this.request(request, null) as Promise<string>;
    }

    /*
     Remove a department
     */

    removeDepartment(departmentId: string): Promise<void> {
        const request = new RestRequest("/api/v1/admin/portal/department/" + departmentId + "", "DELETE", null, undefined);
        return this.requestVoid(request);
    }

    /*
     Remove a location
     */

    removeLocation(locationId: string): Promise<void> {
        const request = new RestRequest("/api/v1/admin/portal/location/" + locationId + "", "DELETE", null, undefined);
        return this.requestVoid(request);
    }

    /*
     Remove a member from a team.
     */

    removeMember(userId: string): Promise<void> {
        const request = new RestRequest("/api/v1/admin/portal/member/" + userId + "", "DELETE", null, undefined);
        return this.requestVoid(request);
    }

    /*
     Remove a team
     */

    removeTeam(teamId: string): Promise<void> {
        const request = new RestRequest("/api/v1/admin/portal/team/" + teamId + "", "DELETE", null, undefined);
        return this.requestVoid(request);
    }

    /*
     Revoke team member invitation
     */

    revokeInviteForUser(member: Member): Promise<string> {
        const request = new RestRequest("/api/v1/admin/portal/revoke/inv", "POST", member, undefined);
        return this.request(request, null) as Promise<string>;
    }

    /*
     Select a company to administer
     */

    selectCompany(companyId: string): Promise<AdminPortal> {
        const request = new RestRequest("/api/v1/admin/portal/select/company/" + companyId + "", "POST", null, undefined);
        return this.request(request, processAdminPortal) as Promise<AdminPortal>;
    }

    /*
     Select a team to administer
     */

    selectTeam(teamId: string): Promise<AdminPortal> {
        const request = new RestRequest("/api/v1/admin/portal/select/team/" + teamId + "", "POST", null, undefined);
        return this.request(request, processAdminPortal) as Promise<AdminPortal>;
    }

    sendCompanyInviteEmails(unsentOnly: boolean): Promise<InviteInfo[]> {
        const request = new RestRequest("/api/v1/admin/portal/company/send/invititations/" + unsentOnly + "", "POST", null, undefined);
        return this.request(request, processInviteInfoArray) as Promise<InviteInfo[]>;
    }

    sendTeamInviteEmails(unsentOnly: boolean): Promise<InviteInfo[]> {
        const request = new RestRequest("/api/v1/admin/portal/team/send/invititations/" + unsentOnly + "", "POST", null, undefined);
        return this.request(request, processInviteInfoArray) as Promise<InviteInfo[]>;
    }

    /*
     Set a department for a user
     */

    setDepartmentForUser(departmentId: string, userId: string): Promise<Department> {
        const request = new RestRequest("/api/v1/admin/portal/department/" + departmentId + "/user/" + userId + "", "POST", null, undefined);
        return this.request(request, null) as Promise<Department>;
    }

    /*
     Set description for team or company
     */

    setDescription(description: string): Promise<void> {
        const request = new RestRequest("/api/v1/admin/portal/description", "POST", description, undefined);
        return this.requestVoid(request);
    }

    /*
     Set a location for a user
     */

    setLocationForUser(locationId: string, userId: string): Promise<Location> {
        const request = new RestRequest("/api/v1/admin/portal/location/" + locationId + "/user/" + userId + "", "POST", null, undefined);
        return this.request(request, null) as Promise<Location>;
    }

    /*
     Set member role. Role is ADMIN or MEMBER
     */

    setMemberRole(userId: string, role: MemberType): Promise<void> {
        const request = new RestRequest("/api/v1/admin/portal/member/" + userId + "/role/" + role + "", "POST", null, undefined);
        return this.requestVoid(request);
    }

    /*
     Set a team for a user
     */

    setTeamForUser(teamId: string, memberUserId: string): Promise<Member> {
        const request = new RestRequest("/api/v1/admin/portal/team/" + teamId + "/user/" + memberUserId + "", "POST", null, undefined);
        return this.request(request, processMember) as Promise<Member>;
    }

    /*
     Update department details
     */

    updateDepartment(department: Department): Promise<Department> {
        const request = new RestRequest("/api/v1/admin/portal/department/update", "POST", department, undefined);
        return this.request(request, null) as Promise<Department>;
    }

    /*
     Update location details
     */

    updateLocation(location: Location): Promise<Location> {
        const request = new RestRequest("/api/v1/admin/portal/location/update", "POST", location, undefined);
        return this.request(request, null) as Promise<Location>;
    }

    /*
     Update team member information
     */

    updateMember(member: Member): Promise<Member> {
        const request = new RestRequest("/api/v1/admin/portal/update/member", "POST", member, undefined);
        return this.request(request, processMember) as Promise<Member>;
    }

    /*
     Update team details
     */

    updateTeam(team: Team): Promise<Team> {
        const request = new RestRequest("/api/v1/admin/portal/team/update", "POST", team, undefined);
        return this.request(request, null) as Promise<Team>;
    }

    /*
     Upload cover image for team or company. Returns cover image URI.
     */

    uploadCoverImage(base64: boolean, imageData: any): Promise<string> {
        const request = new RestRequest("/api/v1/admin/portal/cover/image/" + base64 + "", "POST", imageData, undefined);
        return this.request(request, null) as Promise<string>;
    }

    /*
     Upload logo image for team or company. Returns logo image URI.
     */

    uploadLogoImage(base64: boolean, imageData: any): Promise<string> {
        const request = new RestRequest("/api/v1/admin/portal/logo/image/" + base64 + "", "POST", imageData, undefined);
        return this.request(request, null) as Promise<string>;
    }

}
